import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FieldTip from '../ui/FieldTip';

const FREQUENCY_OPTIONS = [
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
];

const TENOR_OPTIONS = [
  { value: '30_days', label: '30 Days' },
  { value: '60_days', label: '60 Days' },
  { value: '90_days', label: '90 Days' },
  { value: '180_days', label: '180 Days (6 Months)' },
  { value: '270_days', label: '270 Days (9 Months)' },
  { value: '1_year', label: '1 Year' },
  { value: '2_years', label: '2 Years' },
  { value: '3_years', label: '3 Years' },
  { value: '5_years', label: '5 Years' },
  { value: '7_years', label: '7 Years' },
  { value: '10_years', label: '10 Years' },
  { value: '15_years', label: '15 Years' },
  { value: '20_years', label: '20 Years' },
  { value: '30_years', label: '30 Years' },
];

const FD_TENOR_OPTIONS = [
  { value: '30_days', label: '30 Days' },
  { value: '60_days', label: '60 Days' },
  { value: '90_days', label: '90 Days' },
  { value: '180_days', label: '180 Days' },
  { value: '360_days', label: '360 Days' },
];

const BondFields = ({ formData, onFieldChange }) => {
  const isTreasuryBill = formData.type === 'treasury_bill';
  const isFixedDeposit = formData.type === 'fixed_deposit';
  const isBond = formData.type === 'government_bond' || formData.type === 'corporate_bond';
  const isConvertible = formData.type === 'convertible';

  return (
    <>
      {/* Face Value — required for bonds and T-bills */}
      {(isBond || isTreasuryBill) && (
        <div className="space-y-2">
          <Label htmlFor="face_value" className="portfolio-input-label">
            Face Value <span className="text-red-500">*</span>
          </Label>
          <Input
            id="face_value"
            data-testid="face-value-input"
            type="number"
            step="0.01"
            placeholder={isTreasuryBill ? "e.g. 100000 (par value at maturity)" : "e.g. 100000 (par/face value)"}
            value={formData.face_value}
            onChange={(e) => onFieldChange('face_value', e.target.value)}
            className="portfolio-input"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="interest_rate" className="portfolio-input-label">
          {isTreasuryBill ? 'Discount Rate (%)' : 'Interest Rate (%)'} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="interest_rate"
          data-testid="interest-rate-input"
          type="number"
          step="0.01"
          placeholder={isTreasuryBill ? "e.g. 5.25 (annualized discount rate)" : "e.g. 4.25 (annual coupon rate)"}
          value={formData.interest_rate}
          onChange={(e) => onFieldChange('interest_rate', e.target.value)}
          className="portfolio-input"
        />
      </div>

      {/* Coupon Frequency — bonds only (NOT Fixed Deposits) */}
      {isBond && (
        <div className="space-y-2">
          <Label htmlFor="coupon_frequency" className="portfolio-input-label">
            Coupon Frequency <span className="text-red-500">*</span><FieldTip field="coupon_frequency" />
          </Label>
          <Select
            value={formData.coupon_frequency || 'semi_annual'}
            onValueChange={(val) => onFieldChange('coupon_frequency', val)}
          >
            <SelectTrigger data-testid="coupon-frequency-select" className="portfolio-input">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tenor — for bonds, T-bills, convertibles */}
      {(isBond || isTreasuryBill || isConvertible) && (
        <div className="space-y-2">
          <Label htmlFor="tenor" className="portfolio-input-label">Tenor<FieldTip field="tenor" /></Label>
          <Select
            value={formData.tenor || ''}
            onValueChange={(val) => onFieldChange('tenor', val)}
          >
            <SelectTrigger data-testid="tenor-select" className="portfolio-input">
              <SelectValue placeholder="Select tenor" />
            </SelectTrigger>
            <SelectContent>
              {TENOR_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tenor — for Fixed Deposits (shorter options) */}
      {isFixedDeposit && (
        <div className="space-y-2">
          <Label htmlFor="tenor" className="portfolio-input-label">Tenor<FieldTip field="tenor" /></Label>
          <Select
            value={formData.tenor || ''}
            onValueChange={(val) => onFieldChange('tenor', val)}
          >
            <SelectTrigger data-testid="tenor-fd-select" className="portfolio-input">
              <SelectValue placeholder="Select tenor" />
            </SelectTrigger>
            <SelectContent>
              {FD_TENOR_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="maturity_date" className="portfolio-input-label">
          Maturity Date <span className="text-red-500">*</span>
        </Label>
        <Input
          id="maturity_date"
          data-testid="maturity-date-input"
          type="date"
          value={formData.maturity_date}
          onChange={(e) => onFieldChange('maturity_date', e.target.value)}
          className="portfolio-input"
        />
      </div>
    </>
  );
};

export default BondFields;
